// src\themes\default\components\ScrollShowcase.tsx
'use client';

import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion, useScroll, useTransform } from 'motion/react';
import CustomButton from './CustomButton';

type ScrollShowcaseImage = { url: string; description?: string };

type ScrollShowcaseItem = {
    label?: string;
    title?: string;
    description?: string;
    buttonText?: string;
    buttonLink?: string;
    imagePosition?: 'left' | 'right';
    // big_top: یک تصویر بزرگ بالا + دو تصویر کوچیک پایین (مثل اسلاید اول رفرنس)
    // tall_side: دو تصویر کوچیک روی هم یک طرف + یک تصویر کشیده روی کل ارتفاع طرف دیگه
    imageLayout?: 'big_top' | 'tall_side';
    bigImage?: ScrollShowcaseImage;
    smallImage1?: ScrollShowcaseImage;
    smallImage2?: ScrollShowcaseImage;
};

type ScrollShowcaseData = {
    items?: ScrollShowcaseItem[];
};

// دکوراسیون خالدار تزئینی، دقیقاً شبیه چیزی که کنار تیتر توی رفرنس بود
const DotGrid = () => (
    <div className="grid grid-cols-4 gap-2.5 w-fit shrink-0">
        {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className="size-1 rounded-full bg-primary-300" />
        ))}
    </div>
);

function ScrollShowcaseSlide({ item }: { item: ScrollShowcaseItem }) {
    const reversed = item.imagePosition === 'left';

    return (
        <div className="w-full px-4 sm:px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                <div className={reversed ? 'lg:order-2' : ''}>
                    {(item.label || true) && (
                        <div className="flex items-start justify-between gap-6 mb-4">
                            {item.label && (
                                <p className="text-[11px] tracking-[0.2em] uppercase text-primary-500">
                                    {item.label}
                                </p>
                            )}
                            <div className="hidden lg:block ms-auto">
                                <DotGrid />
                            </div>
                        </div>
                    )}
                    {item.title && (
                        <h2 className="text-2xl lg:text-4xl font-light text-primary-900 tracking-tight mb-5 leading-tight">
                            {item.title}
                        </h2>
                    )}
                    {item.description && (
                        <p className="text-[13px] lg:text-sm text-primary-600 leading-7 max-w-md mb-7">
                            {item.description}
                        </p>
                    )}
                    {item.buttonText && (
                        <CustomButton asChild>
                            <Link href={item.buttonLink || '#'}>{item.buttonText}</Link>
                        </CustomButton>
                    )}
                </div>

                {/*
                    ارتفاع تصاویر عمداً بر اساس vh (ارتفاع صفحه) تعیین می‌شه، نه aspect-ratio
                    وابسته به عرض ستون. چون این ویجت توی یک گرید دوستونه قرار داره، اگه ارتفاع
                    از روی عرض ستون محاسبه بشه، روی مانیتورهای عریض ستون‌عکس خیلی پهن می‌شه و
                    ارتفاعش (به‌خصوص توی چیدمان tall_side) به‌شدت از صفحه بیرون می‌زنه. با ارتفاع
                    ثابتِ vh، کل خوشه‌ی تصویر همیشه توی همون کادر پین‌شده جا می‌شه، صرف‌نظر از
                    عرض صفحه، و object-cover هم فقط کراپ افقی بیشتر/کمتری روی عکس اعمال می‌کنه.
                */}
                {item.imageLayout === 'tall_side' ? (
                    <div className={`grid grid-cols-2 grid-rows-2 gap-3 lg:gap-4 h-[46vh] max-h-[420px] ${reversed ? 'lg:order-1' : ''}`}>
                        <div className="relative rounded-lg overflow-hidden bg-primary-100">
                            {item.smallImage1?.url && (
                                <Image
                                    src={item.smallImage1.url}
                                    alt={item.smallImage1.description || ''}
                                    fill
                                    sizes="(max-width: 1024px) 50vw, 25vw"
                                    className="object-cover"
                                />
                            )}
                        </div>
                        <div className="row-span-2 relative rounded-lg overflow-hidden bg-primary-100">
                            {item.bigImage?.url && (
                                <Image
                                    src={item.bigImage.url}
                                    alt={item.bigImage.description || item.title || ''}
                                    fill
                                    sizes="(max-width: 1024px) 50vw, 25vw"
                                    className="object-cover"
                                />
                            )}
                        </div>
                        <div className="relative rounded-lg overflow-hidden bg-primary-100">
                            {item.smallImage2?.url && (
                                <Image
                                    src={item.smallImage2.url}
                                    alt={item.smallImage2.description || ''}
                                    fill
                                    sizes="(max-width: 1024px) 50vw, 25vw"
                                    className="object-cover"
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={`relative grid grid-cols-2 gap-3 lg:gap-4 ${reversed ? 'lg:order-1' : ''}`}>
                        <div className="col-span-2 relative h-[30vh] max-h-[270px] rounded-lg overflow-hidden bg-primary-100">
                            {item.bigImage?.url && (
                                <Image
                                    src={item.bigImage.url}
                                    alt={item.bigImage.description || item.title || ''}
                                    fill
                                    sizes="(max-width: 1024px) 100vw, 50vw"
                                    className="object-cover"
                                />
                            )}
                        </div>
                        <div className="relative h-[16vh] max-h-[140px] rounded-lg overflow-hidden bg-primary-100">
                            {item.smallImage1?.url && (
                                <Image
                                    src={item.smallImage1.url}
                                    alt={item.smallImage1.description || ''}
                                    fill
                                    sizes="(max-width: 1024px) 50vw, 25vw"
                                    className="object-cover"
                                />
                            )}
                        </div>
                        <div className="relative h-[16vh] max-h-[140px] rounded-lg overflow-hidden bg-primary-100">
                            {item.smallImage2?.url && (
                                <Image
                                    src={item.smallImage2.url}
                                    alt={item.smallImage2.description || ''}
                                    fill
                                    sizes="(max-width: 1024px) 50vw, 25vw"
                                    className="object-cover"
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ScrollShowcase({ data }: { data: ScrollShowcaseData }) {
    const items = data?.items || [];
    const containerRef = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    });

    const trackX = useTransform(
        scrollYProgress,
        [0, 1],
        ['0%', `-${Math.max(items.length - 1, 0) * 100}%`]
    );

    if (items.length === 0) return null;

    return (
        <div className="col-span-12">
            {/* موبایل: بدون اسکرول‌جکینگ، به‌صورت بخش‌های عادی زیر هم */}
            <div className="lg:hidden space-y-16">
                {items.map((item, index) => (
                    <ScrollShowcaseSlide key={index} item={item} />
                ))}
            </div>

            {/* دسکتاپ: پین‌شدن سکشن و اسلاید افقی اسلایدها هم‌زمان با اسکرول عمودی */}
            {items.length > 1 ? (
                <div
                    ref={containerRef}
                    className="hidden lg:block relative"
                    style={{ height: `${items.length * 100}vh` }}
                >
                    <div className="sticky top-0 h-screen overflow-hidden">
                        {/*
                            موقعیت‌دهی مطلق با left فیزیکی (نه flex) عمداً انتخاب شده: توی یک
                            صفحه‌ی RTL، اگه اینجا از flex استفاده می‌شد، اسلاید دوم و سوم به‌جای
                            راست/چپِ منطقیِ بعدی، سمت چپِ اسلاید اول قرار می‌گرفتن و با اسکرول
                            (که ترک رو به چپ می‌بره) به‌جای ظاهر شدن، کامل از صفحه خارج می‌شدن.
                            left فیزیکی، برخلاف flex، تحت تأثیر dir صفحه قرار نمی‌گیره.
                        */}
                        <motion.div className="relative h-full w-full" style={{ x: trackX }}>
                            {items.map((item, index) => (
                                <div
                                    key={index}
                                    className="absolute inset-y-0 h-full w-full flex items-center"
                                    style={{ left: `${index * 100}%` }}
                                >
                                    <ScrollShowcaseSlide item={item} />
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            ) : (
                <div className="hidden lg:flex min-h-[80vh] items-center">
                    <ScrollShowcaseSlide item={items[0]} />
                </div>
            )}
        </div>
    );
}
